import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../../../../api';
import Pagination from '../../../../Tools/Pagination';
import VehicleChecklistModal from '../VehicleDailychecklistModal';
import { toast, ToastContainer } from 'react-toastify';

const ManageVehicleChecklist = ({ vehicleId, onClose }) => {
  const [checklistItems, setChecklistItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  const [showEditSubitemModal, setShowEditSubitemModal] = useState(false);
  const [currentEditSubitem, setCurrentEditSubitem] = useState(null);

  /* 
     TOAST HELPERS
      */
  const showToast = (message, isSuccess = true) => {
    if (isSuccess) toast.success(message);
    else toast.error(message);
  };

  /* 
     FETCH CHECKLIST – reads response.data.data
      */
  const fetchChecklistItems = async () => {
    if (!vehicleId) return;

    setIsLoading(true);
    try {
      const token = sessionStorage.getItem('auth_token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axiosInstance.get(
        `ems/start-vehicle-checklist/${vehicleId}`,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const rawItems = response.data?.data ?? [];
      if (!Array.isArray(rawItems)) {
        throw new Error('Invalid data format');
      }

      const formatted = rawItems.map((item) => ({
        id: item.id,
        vehicleChecklistId: item.vehicle_checklist_id, 
        name: item.name,
        criteria: item.criteria?.toString() || '',
        ems_child_vehicle_checklist:
          item.ems_child_vehicle_checklist?.map((c) => ({
            id: c.id,
            vehicleChecklistId: c.vehicle_checklist_id, 
            name: c.name,
            criteria: c.criteria?.toString() || '',
            parent_id: c.parent_id,
          })) || [],
      }));

      // Sort by ID ascending
      formatted.sort((a, b) => a.id - b.id);
      setChecklistItems(formatted);
      setCurrentPage(1);
    } catch (err) {
      console.error('Fetch error:', err);
      showToast(
        'Failed to load checklist: ' +
          (err.response?.data?.message || err.message),
        false
      );
      setChecklistItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChecklistItems();
  }, [vehicleId]);

  /* CRUD HANDLERS */
  const handleAddItem = async () => {
    await fetchChecklistItems();
    setShowAddModal(false);
  };

  const handleUpdateItem = async () => {
    await fetchChecklistItems();
    setShowEditModal(false);
    setCurrentEditItem(null);
  };

  const handleUpdateSubitem = async () => {
    await fetchChecklistItems();
    setShowEditSubitemModal(false);
    setCurrentEditSubitem(null);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const token = sessionStorage.getItem('auth_token');
      await axiosInstance.get(`ems/delete-vehicle-checklist/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Item deleted');
      fetchChecklistItems();
    } catch (err) {
      showToast(
        'Delete failed: ' + (err.response?.data?.message || err.message),
        false
      );
    }
  };

  const handleDeleteSubitem = async (subitemId) => {
    if (!window.confirm('Delete this subitem?')) return;
    try {
      const token = sessionStorage.getItem('auth_token');
      await axiosInstance.get(`ems/delete-vehicle-checklist/${subitemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Subitem deleted');
      fetchChecklistItems();
    } catch (err) {
      showToast(
        'Delete failed: ' + (err.response?.data?.message || err.message),
        false
      );
    }
  };

  /* 
     PAGINATION
      */
  const totalItems = checklistItems.length;
  const paginatedItems = checklistItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => setCurrentPage(page);

  /* 
     RENDER ROWS
      */
  const renderItems = () =>
    paginatedItems.map((item, index) => {
      const serial = (currentPage - 1) * itemsPerPage + index + 1;
      return (
        <React.Fragment key={`item-${item.id}`}>
          {/* Parent */}
          <tr>
            <td>{serial}</td>
            <td>{item.name}</td>
            <td>{item.criteria}</td>
            <td>
              {/* <button
                className="h4 me-2 border-0 bg-transparent"
                onClick={() => {
                  if (item.vehicleChecklistId) {
                    sessionStorage.setItem('vehicle_checklist_id', item.vehicleChecklistId);
                  }
                  setCurrentEditItem(item);
                  setShowEditModal(true);
                }}
                title="Edit"
              >
                <i className="ti ti-edit text-warning" />
              </button> */}
              <button
                className="h4 border-0 bg-transparent"
                onClick={() => handleDelete(item.id)}
                title="Delete"
              >
                <i className="ti ti-trash text-danger" />
              </button>
            </td>
          </tr>

          {/* Children */}
          {item.ems_child_vehicle_checklist.map((child, cIdx) => (
            <tr key={`child-${child.id}`} className="child-row">
              <td></td>
              <td className="ps-4">↳ {child.name}</td>
              <td>{child.criteria}</td>
              <td>
                {/* <button
                  className="h4 me-2 border-0 bg-transparent"
                  onClick={() => {
                    if (child.vehicleChecklistId) {
                      sessionStorage.setItem('vehicle_checklist_id', child.vehicleChecklistId);
                    }
                    setCurrentEditSubitem({
                      id: child.id,
                      name: child.name,
                      criteria: child.criteria,
                      parentId: child.parent_id,
                    });
                    setShowEditSubitemModal(true);
                  }}
                  title="Edit Subitem"
                >
                  <i className="ti ti-edit text-warning" />
                </button> */}
                <button
                  className="h4 border-0 bg-transparent"
                  onClick={() => handleDeleteSubitem(child.id)}
                  title="Delete Subitem"
                >
                  <i className="ti ti-trash text-danger" />
                </button>
              </td>
            </tr>
          ))}
        </React.Fragment>
      );
    });

  /* 
     JSX
      */
  return (
    <>
      <ToastContainer position="top-center" autoClose={4000} />

      {/* Backdrop + Modal */}
      <div
        className="modal fade show"
        style={{ display: 'block' }}
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title">Manage Vehicle Checklist</h5>
              <button
                className="btn btn-outline-primary ms-auto"
                onClick={() => setShowAddModal(true)}
              >
                <i className="ti ti-circle-plus me-2" /> Add Item
              </button>
            </div>

            {/* Body */}
            <div className="modal-body">
              <div className="table-responsive">
                <table className="table mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Criteria</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          <div className="spinner-border text-primary" />
                        </td>
                      </tr>
                    ) : totalItems === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          No checklist items found
                        </td>
                      </tr>
                    ) : (
                      renderItems()
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalItems > itemsPerPage && (
                <div className="mt-3">
                  <Pagination
                    total={totalItems}
                    limit={itemsPerPage}
                    page={currentPage}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button className="btn btn-outline-danger" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay backdrop */}
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
        style={{ zIndex: -1 }}
      />

      {/* Nested Modals */}
      {showAddModal && (
        <VehicleChecklistModal
          vehicleId={vehicleId}
          onClose={() => setShowAddModal(false)}
          refreshData={handleAddItem}
        />
      )}

      {showEditModal && currentEditItem && (
        <VehicleChecklistModal
          vehicleId={vehicleId}
          itemId={currentEditItem.id}
          initialData={{
            ...currentEditItem,
            child: currentEditItem.ems_child_vehicle_checklist || [],
          }}
          onClose={() => {
            setShowEditModal(false);
            setCurrentEditItem(null);
          }}
          refreshData={handleUpdateItem}
        />
      )}

      {showEditSubitemModal && currentEditSubitem && (
        <VehicleChecklistModal
          vehicleId={vehicleId}
          itemId={currentEditSubitem.id}
          parentId={currentEditSubitem.parentId}
          initialData={{
            name: currentEditSubitem.name,
            criteria: currentEditSubitem.criteria,
            child: [],
          }}
          isSubitem={true}
          onClose={() => {
            setShowEditSubitemModal(false);
            setCurrentEditSubitem(null);
          }}
          refreshData={handleUpdateSubitem}
        />
      )}
    </>
  );
};

export default ManageVehicleChecklist;